import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  CreateTableState,
  Grid,
  Icon,
  List,
  Spinner,
  Typography,
} from 'gu-ui-kit'
import styles from 'modules/Recommendations/CampaignsRecommendations/Type/subcomponents/List/list.module.scss'
import configs from './configs'
import {
  DROP_LIST,
  FETCH,
} from 'modules/Recommendations/CampaignsRecommendations/Type/types'
import { useDispatch, useSelector } from 'react-redux'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import GridSpinner from 'components/UI/GridSpinner'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as NoDataFound } from 'images/illustration/no-data-found.svg'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import downloadFile from 'utils/downloadFile'
import { notificationShow } from 'modules/Notifications/actions'
import qs from 'qs'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const ListComponent = ({ type, filterManager }) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const { cancelableDispatch, cancelPreviousRequest } = useCancelToken()
  const [columnVisibility, setColumnVisibility] = useState({})
  const [sorting, setSorting] = useState([])

  const { data, loading, pagination } = useSelector(
    (state) => state['recommendations/type'],
  )

  const { user } = useSelector(({ app }) => app)

  const sortingParams = useMemo(
    () =>
      sorting?.[0]
        ? {
            sort: sorting[0].id,
            direction: sorting[0].desc ? 'desc' : 'asc',
          }
        : {},
    [sorting],
  )

  const fetchData = useCallback(() => {
    cancelPreviousRequest()

    cancelableDispatch({
      type: FETCH,
      payload: {
        type,
        params: {
          user_id: user.id,
          ...filterManager.params,
          offset: pagination.limit * (page - 1),
          limit: pagination.limit,
          ...sortingParams,
        },
      },
    })
  }, [
    cancelPreviousRequest,
    filterManager.params,
    page,
    pagination,
    type,
    sortingParams,
    user,
  ])

  useEffect(() => {
    dispatch({ type: DROP_LIST })
    setPage(1)
  }, [filterManager.params, sorting])

  useEffect(() => {
    if (!filterManager.params) return

    fetchData()
  }, [filterManager.params, sorting, page])

  const handleChangePage = useCallback(() => {
    if (!loading) {
      setPage(page + 1)
    }
  }, [pagination, loading])

  const tableConfig = configs[type]({
    setColumnVisibility,
    setSorting,
  })

  const tableState = CreateTableState(
    {
      ...tableConfig,
      state: {
        columnVisibility,
        sorting,
      },
    },
    data,
  )

  return (
    <>
      <Grid gap={16} alignItems={'center'} className={'margin-bottom-16'}>
        <Button
          startIcon={<Icon name={'file-xls'} />}
          grey
          variant={'ghost'}
          size={'m'}
          onClick={() =>
            downloadFile(
              `/api/app/v2/yd/recommendations/${type}/file?${qs.stringify({
                sort: sorting[0]?.id,
                direction: sorting[0]?.desc ? 'desc' : 'asc',
                ...filterManager.params,
              })}`,
              'getuniq-recommendations-' + type,
              'xls',
            ).catch((error) =>
              dispatch(
                notificationShow({
                  type: 'warning',
                  message: error.message,
                  delay: 20000,
                }),
              ),
            )
          }
        >
          {t('recommendations::type.export_button.label')}
        </Button>
        <Typography
          variant={breakpoints.sm ? 'Body-5' : 'Body-15'}
          color={'Label-Secondary'}
        >
          {t('recommendations::type.currency_notification')}
        </Typography>
      </Grid>
      <div className={styles['table-wrapper']}>
        {loading && data.length === 0 ? (
          <GridSpinner width={30} />
        ) : data.length === 0 ? (
          <EmptyResults
            icon={<NoDataFound />}
            header={t('recommendations::type.table.empty_list.header')}
            text={t('recommendations::type.table.empty_list.text')}
          />
        ) : (
          <List loading={loading} table={tableState} config={tableConfig} />
        )}
      </div>
      {!(data.length === 0 && loading) && pagination.total > data.length && (
        <div className={'margin-top-32'}>
          <Grid alignItems={'center'} justifyContent={'center'}>
            <Button
              size={'m'}
              disabled={loading}
              onClick={handleChangePage}
              variant={'secondary'}
              startIcon={
                loading ? <Spinner width={16} color={'inherit'} /> : null
              }
            >
              {t('common::pagination.show_more')}
            </Button>
          </Grid>
        </div>
      )}
    </>
  )
}

export default ListComponent

ListComponent.propTypes = {
  type: PropTypes.string,
  filterManager: PropTypes.object,
  data: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  sorting: PropTypes.object,
}
